import React from 'react';
import Head from 'next/head';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Link from 'next/link';
import Image from 'next/image';
import LiveDemoSection from '../components/LiveDemoSection';
import HowItWorksSection from '../components/HowItWorksSection';
import XlimIntroSection from '../components/XlimIntroSection';

export default function ForCreativeAuthorities() {
  return (
    <>
      <Head>
        <title>For Creative Authorities | Personalia - InDesign Automation Platform</title>
        <meta name="description" content="Outgrown Bannerbear or PandaDoc? Personalia preserves your design integrity with true InDesign automation — not web-canvas approximations." />
      </Head>

      <Header />

      <main id="main-content">
        {/* Hero Section */}
        <section className="bg-gray-900 text-white py-16 md:py-24">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary bg-opacity-20 text-primary-light rounded-full px-4 py-2 text-sm font-medium mb-6">
                Tired of rebuilding designs in web editors?
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Turn Your InDesign Files Into an Automated Production Pipeline.
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Most automation tools force designers to become “template builders” in clunky web editors.
                Personalia lets you design once in InDesign and instantly produce every version you need, at scale.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="#live-demo" className="btn btn-primary btn-lg">
                  Try Live Demo
                </a>
                <Link href="/contact" className="btn btn-outline-light btn-lg">
                  Get In Contact
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Design Benefits Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                You Design. Personalia Delivers.
              </h2>
              <p className="text-xl text-gray-600">
                Personalia helps preserve your design intent from concept to output. You focus on design — we handle the IT infrastructure and scalability to produce assets on-demand, from one to hundreds of thousands.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                <div className="bg-primary bg-opacity-10 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Design Integrity</h3>
                <p className="text-gray-600">
                  Craft templates in Adobe InDesign that preserve your creative intent — every asset stays true to your brand standards.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                <div className="bg-primary bg-opacity-10 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Speed-to-Market</h3>
                <p className="text-gray-600">
                  Generate hundreds or thousands of personalized variations in minutes. Personalia's cloud infrastructure handles the production so your campaigns launch faster.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                <div className="bg-primary bg-opacity-10 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Cost Savings</h3>
                <p className="text-gray-600">
                  Eliminate repetitive design work and IT overhead. No servers to manage, no software to install — just create once, reuse endlessly.
                </p>
              </div>
            </div>
          </div>
        </section>

        <HowItWorksSection userType="designer" />

        {/* Why Switch Section - Competitive Positioning */}
        <section className="py-16 md:py-24 bg-white border-t border-gray-100">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Outgrown Your Current Tools?
              </h2>
              <p className="text-xl text-gray-600">
                If you've hit the ceiling with web-based design automation, you're not alone.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Web-Canvas Pain Point */}
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="text-sm font-semibold text-red-500 uppercase tracking-wide mb-3">The Problem</div>
                <h3 className="text-xl font-bold mb-4">Web-Canvas Tools Fall Short</h3>
                <p className="text-gray-600 mb-4">
                  Web-based automation tools use a browser to "take a screenshot" of your design. 
                  The result? Fonts render differently, layouts shift, and advanced typography is impossible.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    No GREP styles or nested styles
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    No optical kerning or complex ligatures
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    No print-ready output (CMYK, bleeds, spot colours)
                  </li>
                </ul>
              </div>

              {/* Personalia Solution */}
              <div className="bg-primary rounded-2xl p-8 text-white">
                <div className="text-sm font-semibold text-primary-light uppercase tracking-wide mb-3">The Personalia Difference</div>
                <h3 className="text-xl font-bold mb-4">Server-Side InDesign Rendering</h3>
                <p className="opacity-90 mb-4">
                  Personalia renders your InDesign templates server-side with full fidelity — not a web browser. 
                  If it looks right in InDesign, it looks right in the output. Every time.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-primary-light mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Full support for every InDesign typography feature
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-primary-light mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Press-ready output with full colour management
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-4 w-4 text-primary-light mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Trusted by enterprise teams worldwide
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center mt-10">
              <Link href="/why-personalia" className="text-primary hover:underline font-medium inline-flex items-center gap-2">
                See the full comparison
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* InDesign Integration Section */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="md:w-1/2">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Adobe InDesign Integration
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Design in Adobe InDesign, then package your templates for Personalia. Our platform supports the most commonly used InDesign features, letting you stay in the creative environment you're familiar with.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700">Design templates in InDesign and package them for use with Personalia</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700">Control dynamic images, text, paragraph and object styles</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700">Define visibility rules for layers and spreads</p>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-primary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-gray-700">Implement business rules and logic within the template</p>
                  </li>
                </ul>
              </div>
              <div className="md:w-1/2">
                <div className="relative rounded-lg overflow-hidden shadow-xl">
                  <Image
                    src="/images/indesign-integration.webp"
                    alt="Adobe InDesign Integration"
                    width={600}
                    height={400}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <XlimIntroSection variant="designer" />

        {/* Testimonial Section */}
        <section className="py-16 md:py-24 bg-primary text-white">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <svg className="h-12 w-12 mx-auto mb-6 text-white opacity-80" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
              <p className="text-2xl mb-8 leading-relaxed">
                "Implementing Personalia has been a game-changer for our design team. We can now create beautiful, on-brand materials at scale with consistent quality. Our production time has decreased by 70% while maintaining our high design standards."
              </p>
              <div>
                <p className="font-semibold text-xl">Sarah Johnson</p>
                <p className="text-white text-opacity-80">Creative Director</p>
              </div>
            </div>
          </div>
        </section>

        <LiveDemoSection />

        {/* Digital and Print Section */}
        <section className="py-16 md:py-24 bg-gray-900 text-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                One Template, Multiple Outputs
              </h2>
              <p className="text-xl text-gray-300">
                Design once and deliver content optimized for both digital and print channels.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white bg-opacity-5 rounded-xl p-6 border border-white border-opacity-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-primary bg-opacity-20 p-2 rounded-lg">
                      <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold">Digital Assets</h3>
                  </div>
                  <p className="text-gray-300 text-sm">Automatically generate RGB PDFs, JPGs, or PNGs with optimal file size for email and digital distribution.</p>
                </div>

                <div className="bg-white bg-opacity-5 rounded-xl p-6 border border-white border-opacity-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-primary bg-opacity-20 p-2 rounded-lg">
                      <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold">Print-Ready Files</h3>
                  </div>
                  <p className="text-gray-300 text-sm">Generate high-resolution 300 DPI PDFs with bleeds, crops, and spot colours for professional printing.</p>
                </div>

                <div className="bg-white bg-opacity-5 rounded-xl p-6 border border-white border-opacity-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-primary bg-opacity-20 p-2 rounded-lg">
                      <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold">Brand Consistency</h3>
                  </div>
                  <p className="text-gray-300 text-sm">Maintain perfect colour accuracy across all channels, from RGB for screens to CMYK and spot colours for print.</p>
                </div>

                <div className="bg-white bg-opacity-5 rounded-xl p-6 border border-white border-opacity-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-primary bg-opacity-20 p-2 rounded-lg">
                      <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold">Time Savings</h3>
                  </div>
                  <p className="text-gray-300 text-sm">Reduce production time by eliminating the need to create separate designs for digital and print.</p>
                </div>
              </div>

              <div className="text-center mt-10">
                <Link href="/contact" className="btn btn-primary btn-lg">
                  Talk to a Specialist
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Transform Your Design Workflow?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Start creating templates that maintain your design integrity while saving time and resources.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link href="/contact" className="btn btn-primary btn-lg">
                  Request a Demo
                </Link>
                <Link href="/pricing" className="btn btn-outline-dark btn-lg">
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
